import React from 'react';
import { BadgeProps } from '@frontend/shadcn/components/ui/badge';
import { Cross2Icon } from '@radix-ui/react-icons';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemKey,
  TooltipItemValue,
  TooltipContent,
} from '../../../components/tooltip';
import { DismissableBadge } from '@/applications/Social/components/filters/badges/dismissable-badge';

export interface DemographicData {
  id: string;
  names: string[];
}

export interface BadgeContentProps {
  icon?: React.ReactNode;
  label: string;
  names: string[];
  formatNames: (names: string[]) => string;
}

export const BadgeContent: React.FC<BadgeContentProps> = ({ icon, label, names, formatNames }) => (
  <>
    {icon}
    {label}:<span className="capitalize ml-1">{formatNames([names[0]])}</span>
    {names.length > 1 && <span className="ml-1">+{names.length - 1}</span>}
  </>
);

export interface BadgeDismissButtonProps {
  onDismiss: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  label: string;
  dismissActionName: string;
}

export const BadgeDismissButton: React.FC<BadgeDismissButtonProps> = ({ onDismiss, label, dismissActionName }) => (
  <>
    <button
      type="button"
      onClick={onDismiss}
      aria-label={`Remove ${label.toLowerCase()} filter`}
      className="flex ml-1 p-1 focus:outline-none focus:ring"
      data-dd-action-name={dismissActionName}
    >
      <Cross2Icon className="h-3 w-3" />
    </button>
    <span className="sr-only">
      Remove {label.toLowerCase()} filter for {label}
    </span>
  </>
);

export interface BadgeTooltipProps {
  tooltipTitle: string;
  label: string;
  names: string[];
  formatNames: (names: string[]) => string;
}

export const BadgeTooltip: React.FC<BadgeTooltipProps> = ({ tooltipTitle, label, names, formatNames }) => (
  <TooltipContent>
    <TooltipHeader>
      <TooltipTitle>{tooltipTitle}</TooltipTitle>
    </TooltipHeader>
    <TooltipBody>
      <TooltipItem>
        <TooltipItemKey>Selected {label}s</TooltipItemKey>
        <TooltipItemValue>
          {formatNames(names)}
        </TooltipItemValue>
      </TooltipItem>
    </TooltipBody>
  </TooltipContent>
);

export interface BaseBadgeWrapperProps extends BadgeProps {
  id: string;
  actionName: string;
  children: React.ReactNode;
}

export const BaseBadgeWrapper = React.forwardRef<HTMLDivElement, BaseBadgeWrapperProps>(
  ({ id, actionName, children, ...props }, ref) => (
    <DismissableBadge
      ref={ref}
      key={id}
      id={id}
      variant="secondary"
      className="mr-2 mb-2"
      role="button"
      data-dd-action-name={actionName}
      {...props}
    >
      {children}
    </DismissableBadge>
  )
);

BaseBadgeWrapper.displayName = 'BaseBadgeWrapper';
